'use client'

import { forwardRef, useState, useEffect, useRef, useCallback } from 'react'
import { Calendar, ChevronDown, X, Check, RotateCcw } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Date Range Picker Component
 * 
 * Enhanced date range picker implementing all 10 BIRHAUS principles:
 * - Spanish-first date formatting (DD/MM/YYYY)
 * - Progressive disclosure with presets and custom range
 * - Cognitive load reduction with max 30-day default ranges
 * - Accessibility compliance (WCAG AA+)
 * - Undo functionality for significant date changes
 * - Auto-validation for business rules
 * - IPU Admin specific presets for reports
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// Date utilities for Spanish-first formatting
const MESES_ESPANOL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

const DIAS_CORTOS_ESPANOL = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

export interface DateRange {
  start: Date | null
  end: Date | null
}

export interface PresetRange {
  id: string
  labelEs: string
  labelEn: string
  getValue: () => DateRange
  isDefault?: boolean
}

interface UndoState {
  previousRange: DateRange
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

export interface BirhausDateRangePickerProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Value management
  value?: DateRange
  defaultValue?: DateRange
  onChange?: (range: DateRange) => void
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Validation
  minDate?: Date
  maxDate?: Date
  maxRangeDays?: number // Max days in range (default 30)
  
  // Presets for IPU Admin
  presets?: PresetRange[]
  showPresets?: boolean
  enableCustomRange?: boolean
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  required?: boolean
  placeholder?: string
  
  // BIRHAUS Principle #8: Performance is UX
  autoApply?: boolean // Apply immediately vs requiring confirmation
  
  // Cognitive load
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'minimal'
  
  // Locale
  locale?: 'es' | 'en'
  
  // IPU Admin specific
  reportType?: 'financial' | 'membership' | 'events' | 'custom'
}

export const BirhausDateRangePicker = forwardRef<HTMLDivElement, BirhausDateRangePickerProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // Value
  value,
  defaultValue,
  onChange,
  
  // Validation
  error,
  errorEs,
  errorEn,
  minDate,
  maxDate,
  maxRangeDays = 30,
  
  // Presets
  presets,
  showPresets = true,
  enableCustomRange = true,
  
  // Undo
  enableUndo = true,
  undoTimeoutMs = 10000,
  
  // Basic props
  disabled = false,
  required = false,
  placeholder,
  autoApply = false,
  
  // Appearance
  size = 'md',
  variant = 'default',
  
  // Locale
  locale = 'es',
  
  // IPU Admin
  reportType = 'custom'
}, ref) => {
  // State management
  const [internalRange, setInternalRange] = useState<DateRange>(
    value || defaultValue || { start: null, end: null }
  )
  const [isOpen, setIsOpen] = useState(false)
  const [activePreset, setActivePreset] = useState<string>('')
  const [customMode, setCustomMode] = useState(false)
  const [undoState, setUndoState] = useState<UndoState>({
    previousRange: { start: null, end: null },
    timeout: null,
    isActive: false
  })
  const [validationError, setValidationError] = useState<string>('')
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayError = error || errorEs || validationError
  
  // Default presets for IPU Admin
  const getDefaultPresets = useCallback((): PresetRange[] => {
    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    
    const basePresets: PresetRange[] = [
      {
        id: 'today',
        labelEs: 'Hoy',
        labelEn: 'Today',
        getValue: () => ({
          start: startOfToday,
          end: startOfToday
        })
      },
      {
        id: 'week',
        labelEs: 'Última semana',
        labelEn: 'Last week',
        getValue: () => {
          const end = new Date(startOfToday)
          const start = new Date(end)
          start.setDate(start.getDate() - 6)
          return { start, end }
        },
        isDefault: true
      },
      {
        id: 'month',
        labelEs: 'Último mes',
        labelEn: 'Last month',
        getValue: () => {
          const end = new Date(startOfToday)
          const start = new Date(end)
          start.setDate(start.getDate() - 29)
          return { start, end }
        }
      },
      {
        id: 'quarter',
        labelEs: 'Último trimestre',
        labelEn: 'Last quarter',
        getValue: () => {
          const end = new Date(startOfToday)
          const start = new Date(end)
          start.setDate(start.getDate() - 89)
          return { start, end }
        }
      }
    ]
    
    // Add report-specific presets
    if (reportType === 'financial') {
      basePresets.push({
        id: 'fiscal_year',
        labelEs: 'Año fiscal actual',
        labelEn: 'Current fiscal year',
        getValue: () => {
          const end = new Date(startOfToday)
          const start = new Date(end.getFullYear(), 0, 1) // January 1st
          return { start, end }
        }
      })
    }
    
    if (reportType === 'membership') {
      basePresets.push({
        id: 'year_to_date',
        labelEs: 'Lo que va del año',
        labelEn: 'Year to date',
        getValue: () => {
          const end = new Date(startOfToday)
          const start = new Date(end.getFullYear(), 0, 1)
          return { start, end }
        }
      })
    }
    
    return basePresets
  }, [reportType])
  
  const allPresets = presets || getDefaultPresets()
  
  // Format date for display (DD/MM/YYYY for Spanish)
  const formatDate = useCallback((date: Date | null): string => {
    if (!date) return ''
    
    if (locale === 'es') {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      })
    }
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }, [locale])
  
  // Validation function
  const validateRange = useCallback((range: DateRange): string => {
    if (!range.start || !range.end) {
      return locale === 'es' 
        ? 'Selecciona fechas de inicio y fin'
        : 'Select start and end dates'
    }
    
    if (range.start > range.end) {
      return locale === 'es'
        ? 'La fecha de inicio debe ser anterior a la fecha de fin'
        : 'Start date must be before end date'
    }
    
    if (minDate && range.start < minDate) {
      return locale === 'es'
        ? `La fecha de inicio no puede ser anterior a ${formatDate(minDate)}`
        : `Start date cannot be before ${formatDate(minDate)}`
    }
    
    if (maxDate && range.end > maxDate) {
      return locale === 'es'
        ? `La fecha de fin no puede ser posterior a ${formatDate(maxDate)}`
        : `End date cannot be after ${formatDate(maxDate)}`
    }
    
    const daysDiff = Math.ceil((range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60 * 24))
    if (daysDiff > maxRangeDays) {
      return locale === 'es'
        ? `El rango no puede exceder ${maxRangeDays} días`
        : `Range cannot exceed ${maxRangeDays} days`
    }
    
    return ''
  }, [minDate, maxDate, maxRangeDays, formatDate, locale])
  
  // Handle undo functionality
  const handleUndo = useCallback(() => {
    if (undoState.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      setInternalRange(undoState.previousRange)
      onChange?.(undoState.previousRange)
      setUndoState({ previousRange: { start: null, end: null }, timeout: null, isActive: false })
    }
  }, [undoState, onChange])
  
  // Setup undo for significant changes
  const setupUndo = useCallback((newRange: DateRange, oldRange: DateRange) => {
    if (!enableUndo) return
    
    // Check if this is a significant change (different dates)
    const isSignificant = (
      newRange.start?.getTime() !== oldRange.start?.getTime() ||
      newRange.end?.getTime() !== oldRange.end?.getTime()
    )
    
    if (isSignificant && (oldRange.start || oldRange.end)) {
      // Clear existing timeout
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
      
      // Set up new undo state
      const timeout = setTimeout(() => {
        setUndoState(prev => ({ ...prev, isActive: false, timeout: null }))
      }, undoTimeoutMs)
      
      setUndoState({
        previousRange: oldRange,
        timeout,
        isActive: true
      })
    }
  }, [enableUndo, undoTimeoutMs, undoState.timeout])
  
  // Handle preset selection
  const handlePresetSelect = useCallback((preset: PresetRange) => {
    const oldRange = { ...internalRange }
    const newRange = preset.getValue()
    
    setInternalRange(newRange)
    setActivePreset(preset.id)
    setCustomMode(false)
    setValidationError('')
    
    // Setup undo
    setupUndo(newRange, oldRange)
    
    if (autoApply) {
      onChange?.(newRange)
      setIsOpen(false)
    }
  }, [internalRange, autoApply, onChange, setupUndo])
  
  // Handle custom range changes
  const handleCustomRangeChange = useCallback((field: 'start' | 'end', date: Date | null) => {
    const oldRange = { ...internalRange }
    const newRange = { ...internalRange, [field]: date }
    
    setInternalRange(newRange)
    setActivePreset('')
    setCustomMode(true)
    
    // Validate
    const errorMessage = validateRange(newRange)
    setValidationError(errorMessage)
    
    // Setup undo if valid and complete
    if (!errorMessage && newRange.start && newRange.end) {
      setupUndo(newRange, oldRange)
      
      if (autoApply) {
        onChange?.(newRange)
      }
    }
  }, [internalRange, validateRange, setupUndo, autoApply, onChange])
  
  // Apply range (for non-auto apply mode)
  const handleApply = useCallback(() => {
    const errorMessage = validateRange(internalRange)
    if (!errorMessage) {
      onChange?.(internalRange)
      setIsOpen(false)
    } else {
      setValidationError(errorMessage)
    }
  }, [internalRange, validateRange, onChange])
  
  // Display text for selected range
  const getDisplayValue = useCallback((): string => {
    if (!internalRange.start && !internalRange.end) {
      return placeholder || (locale === 'es' ? 'Seleccionar rango de fechas' : 'Select date range')
    }
    
    if (internalRange.start && internalRange.end) {
      if (locale === 'es') {
        return `${formatDate(internalRange.start)} - ${formatDate(internalRange.end)}`
      }
      return `${formatDate(internalRange.start)} to ${formatDate(internalRange.end)}`
    }
    
    return locale === 'es' ? 'Rango incompleto' : 'Incomplete range'
  }, [internalRange, formatDate, placeholder, locale])
  
  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])
  
  // Sync external value changes
  useEffect(() => {
    if (value && (
      value.start?.getTime() !== internalRange.start?.getTime() ||
      value.end?.getTime() !== internalRange.end?.getTime()
    )) {
      setInternalRange(value)
    }
  }, [value, internalRange])
  
  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
    }
  }, [undoState.timeout])
  
  // Style classes
  const sizeClasses = {
    sm: 'text-sm px-2 py-1',
    md: 'text-base px-3 py-2', 
    lg: 'text-lg px-4 py-3'
  }
  
  const variantClasses = {
    default: 'bg-white border border-gray-300',
    minimal: 'bg-transparent border-b border-gray-300'
  }
  
  return (
    <div ref={ref} className="birhaus-date-range-picker">
      {/* Label */}
      {displayLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-sm text-gray-600 mb-2">
          {displayDescription}
        </p>
      )}
      
      {/* Main Input */}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'w-full flex items-center justify-between rounded-md transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
            safeAccess(sizeClasses, size, sizeClasses.md),
            safeAccess(variantClasses, variant, variantClasses.default),
            displayError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-primary-500',
            disabled && 'cursor-not-allowed bg-gray-50 text-gray-400'
          )}
        >
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className={cn(
              !internalRange.start && !internalRange.end && 'text-gray-400'
            )}>
              {getDisplayValue()}
            </span>
          </div>
          <ChevronDown className={cn(
            'h-4 w-4 text-gray-400 transition-transform duration-200',
            isOpen && 'transform rotate-180'
          )} />
        </button>
        
        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full min-w-[320px] bg-white border border-gray-200 rounded-md shadow-lg"
          >
            <div className="p-4">
              {/* Presets */}
              {showPresets && allPresets.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {locale === 'es' ? 'Rangos predefinidos' : 'Quick ranges'}
                  </h4>
                  <div className="space-y-1">
                    {allPresets.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handlePresetSelect(preset)}
                        className={cn(
                          'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                          'hover:bg-gray-100',
                          activePreset === preset.id
                            ? 'bg-primary-50 text-primary-700 border border-primary-200'
                            : 'text-gray-700'
                        )}
                      >
                        {locale === 'es' ? preset.labelEs : preset.labelEn}
                        {preset.isDefault && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({locale === 'es' ? 'recomendado' : 'recommended'})
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Custom Range */}
              {enableCustomRange && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {locale === 'es' ? 'Rango personalizado' : 'Custom range'}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {locale === 'es' ? 'Desde' : 'From'}
                      </label>
                      <input
                        type="date"
                        value={internalRange.start ? internalRange.start.toISOString().split('T')[0] : ''}
                        onChange={(e) => handleCustomRangeChange('start', e.target.value ? new Date(e.target.value) : null)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {locale === 'es' ? 'Hasta' : 'To'}
                      </label>
                      <input
                        type="date"
                        value={internalRange.end ? internalRange.end.toISOString().split('T')[0] : ''}
                        onChange={(e) => handleCustomRangeChange('end', e.target.value ? new Date(e.target.value) : null)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Error Display */}
              {displayError && (
                <div className="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded-md">
                  {displayError}
                </div>
              )}
              
              {/* Actions */}
              {!autoApply && (
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    {locale === 'es' ? 'Cancelar' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!!validationError}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-md',
                      'bg-primary-600 text-white hover:bg-primary-700',
                      'disabled:bg-gray-300 disabled:cursor-not-allowed',
                      'flex items-center space-x-1'
                    )}
                  >
                    <Check className="h-3 w-3" />
                    <span>{locale === 'es' ? 'Aplicar' : 'Apply'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {displayError && !isOpen && (
        <p className="mt-1 text-sm text-red-600">
          {displayError}
        </p>
      )}
      
      {/* Undo Notification */}
      {undoState.isActive && (
        <div className="mt-2 bg-gray-800 text-white text-sm rounded-md p-3 flex items-center justify-between">
          <span>
            {locale === 'es' ? 'Rango de fechas cambiado' : 'Date range changed'}
          </span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>{locale === 'es' ? 'Deshacer' : 'Undo'}</span>
          </button>
        </div>
      )}
    </div>
  )
})

BirhausDateRangePicker.displayName = 'BirhausDateRangePicker'

// Hook for enhanced date range functionality
export const useBirhausDateRange = (initialRange?: DateRange) => {
  const [range, setRange] = useState<DateRange>(
    initialRange || { start: null, end: null }
  )
  const [history, setHistory] = useState<DateRange[]>([range])
  const [historyIndex, setHistoryIndex] = useState(0)
  
  const updateRange = useCallback((newRange: DateRange) => {
    setRange(newRange)
    const newHistory = history.slice(0, historyIndex + 1)
    newHistory.push(newRange)
    setHistory(newHistory.slice(-10)) // Keep last 10 states
    setHistoryIndex(newHistory.length - 1)
  }, [history, historyIndex])
  
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      const range = safeArrayAccess(history, newIndex, null)
      if (range) {
        setRange(range)
        return range
      }
    }
    return null
  }, [history, historyIndex])
  
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1
      setHistoryIndex(newIndex)
      const range = safeArrayAccess(history, newIndex, null)
      if (range) {
        setRange(range)
        return range
      }
    }
    return null
  }, [history, historyIndex])
  
  const getDaysBetween = useCallback(() => {
    if (!range.start || !range.end) return 0
    return Math.ceil((range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60 * 24)) + 1
  }, [range])
  
  const canUndo = historyIndex > 0
  const canRedo = historyIndex < history.length - 1
  
  return {
    range,
    setRange: updateRange,
    undo,
    redo,
    canUndo,
    canRedo,
    daysBetween: getDaysBetween(),
    isValid: range.start && range.end && range.start <= range.end
  }
}

// IPU Admin specific date range presets
export const ipuAdminDatePresets = {
  financial: (): PresetRange[] => [
    {
      id: 'current_month',
      labelEs: 'Mes actual',
      labelEn: 'Current month',
      getValue: () => {
        const now = new Date()
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        return { start, end }
      }
    },
    {
      id: 'last_month',
      labelEs: 'Mes anterior',
      labelEn: 'Last month',
      getValue: () => {
        const now = new Date()
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0)
        return { start, end }
      }
    },
    {
      id: 'quarter',
      labelEs: 'Trimestre actual',
      labelEn: 'Current quarter',
      getValue: () => {
        const now = new Date()
        const quarter = Math.floor(now.getMonth() / 3)
        const start = new Date(now.getFullYear(), quarter * 3, 1)
        const end = new Date(now.getFullYear(), (quarter + 1) * 3, 0)
        return { start, end }
      }
    }
  ],
  
  events: (): PresetRange[] => [
    {
      id: 'next_7_days',
      labelEs: 'Próximos 7 días',
      labelEn: 'Next 7 days',
      getValue: () => {
        const start = new Date()
        const end = new Date()
        end.setDate(end.getDate() + 6)
        return { start, end }
      }
    },
    {
      id: 'next_30_days',
      labelEs: 'Próximos 30 días',
      labelEn: 'Next 30 days',
      getValue: () => {
        const start = new Date()
        const end = new Date()
        end.setDate(end.getDate() + 29)
        return { start, end }
      }
    }
  ]
}